import { useEffect } from 'react'

/**
 * Undoes the `aria-hidden` Base UI puts on the rest of the page for a popup
 * that is not modal.
 *
 * A non-modal popup leaves the document usable: the reader can still scroll,
 * still click the next filter, still tab out. Base UI hides everything outside
 * it from assistive technology anyway, and does not make it `inert` — so a
 * keyboard user tabs straight into controls a screen reader has just been told
 * do not exist. axe reports it as `aria-hidden-focus`, nine nodes, on every
 * screen with a filter bar. Upstream: mui/base-ui#4678.
 *
 * The library's own answer is `modal`, and it was measured before this was
 * written: it fixes the report and costs two clicks instead of one to move
 * between filters, because the backdrop swallows the first. On a bar with five
 * of them that is the wrong trade.
 *
 * So the mismatch is settled the other way — the background stays visible to
 * everyone, which is what «not modal» means. Only the elements this popup hid
 * are touched: anything already `aria-hidden` before it opened was hidden by
 * the application on purpose and is left alone.
 */
export function useVisibleBackground(open: boolean): void {
  useEffect(() => {
    if (!open) return

    const alreadyHidden = new Set(document.querySelectorAll('[aria-hidden="true"]'))

    // After paint: the attribute is applied as the popup mounts.
    const frame = requestAnimationFrame(() => {
      for (const element of document.querySelectorAll('[aria-hidden="true"]')) {
        if (!alreadyHidden.has(element)) element.removeAttribute('aria-hidden')
      }
    })

    // Nothing to restore on close — Base UI removes what it set, and what it
    // set is already gone.
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [open])
}
